import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { BbotButton, BbotModal, Checkbox, Input, PageLoadingSpinner, Space } from "top-component-library";
import DisconnectIntegrationDialog from "./DisconnectIntegrationDialog";
import { FormRow } from "./styles";

// Mirrors add-integration-dialog.html's Omnivore MMS form. Reachable only once already
// connected (its row on the settings grid only renders when enabled), so the diagnostics
// buttons' legacy omnivoreAbleToBeDiagnosed guard (only true once connected) is always
// satisfied here - dropped as always-true rather than ported as dead weight. The "Show
// Details" button is skipped entirely: its guard variable is never set anywhere in the
// legacy app.
const OmnivoreMmsDialog = ({
  visible,
  isAdmin,
  settings,
  onClose,
  onLoad,
  onSave,
  onDisconnect,
  onCheckDiagnostic,
  onDownloadPosItems,
}) => {
  const [localSettings, setLocalSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [disconnectVisible, setDisconnectVisible] = useState(false);
  // Tracks which settings object has already been seeded, so a reopen can't briefly show
  // (and let Submit act on) the hook's still-stale cache from before this open's onLoad()
  // resolves - localSettings is cleared to null on every open and only repopulated once a
  // genuinely new settings reference arrives.
  const consumedSettingsRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setLocalSettings(null);
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible && settings && settings !== consumedSettingsRef.current) {
      consumedSettingsRef.current = settings;
      setLocalSettings(settings);
    }
  }, [visible, settings]);

  const handleSave = async () => {
    setSaving(true);
    const succeeded = await onSave(localSettings);
    setSaving(false);
    if (succeeded) onClose();
  };

  return (
    <BbotModal
      title="Omnivore MMS Integration Settings"
      visible={visible}
      onCancel={onClose}
      footer={[
        isAdmin && (
          <BbotButton key="disconnect" danger onClick={() => setDisconnectVisible(true)}>
            Disconnect
          </BbotButton>
        ),
        <BbotButton key="submit" type="primary" loading={saving} onClick={handleSave}>
          Submit
        </BbotButton>,
        <BbotButton key="close" onClick={onClose}>
          Close
        </BbotButton>,
      ]}
    >
      {!localSettings ? (
        <PageLoadingSpinner />
      ) : (
        <div>
          <FormRow>
            <label htmlFor="omnivore-location-id">Omnivore Location Id</label>
            <Input
              id="omnivore-location-id"
              value={localSettings.omnivore_location_id}
              onChange={(event) => {
                const { value } = event.target;
                setLocalSettings((current) => ({ ...current, omnivore_location_id: value }));
              }}
            />
          </FormRow>
          <Checkbox
            checked={Boolean(localSettings.inject_orders)}
            onChange={(event) => setLocalSettings((current) => ({ ...current, inject_orders: event.target.checked }))}
          >
            Inject orders into POS?
          </Checkbox>
          {isAdmin && (
            <FormRow>
              <Space>
                <BbotButton onClick={onDownloadPosItems}>See POS Items</BbotButton>
                <BbotButton onClick={onCheckDiagnostic}>Check Menu Diagnostic</BbotButton>
              </Space>
            </FormRow>
          )}
        </div>
      )}
      <DisconnectIntegrationDialog
        visible={disconnectVisible}
        integrationName="Omnivore MMS"
        onClose={() => setDisconnectVisible(false)}
        onConfirm={async (deleteMenuItems) => {
          const succeeded = await onDisconnect(deleteMenuItems);
          if (succeeded) onClose();
          return succeeded;
        }}
      />
    </BbotModal>
  );
};

OmnivoreMmsDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  settings: PropTypes.shape({
    omnivore_location_id: PropTypes.string,
    inject_orders: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
  onCheckDiagnostic: PropTypes.func.isRequired,
  onDownloadPosItems: PropTypes.func.isRequired,
};

OmnivoreMmsDialog.defaultProps = {
  settings: null,
};

export default OmnivoreMmsDialog;
